import React, { useState } from 'react';
import { IconButton, Button, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SaveIcon from '@mui/icons-material/Save';
import DownloadIcon from '@mui/icons-material/Download';
import i18next from 'i18next';

interface OfferActionMenuProps {
  canSave: boolean;
  canDownload: boolean;
  onSave: () => void;
  onDownload: () => void;
  disabled?: boolean;
  /** True when PDF is reservation confirmation (incl. hotel cancel pending / confirmed). */
  useReservationConfirmationDownloadLabel: boolean;
  /** When set, download is cancellation confirmation (hotel confirmed cancel). */
  useCancellationConfirmationDownloadLabel?: boolean;
  isEditorDirty: boolean;
}

/**
 * Action menu dropdown for offer actions (save, download).
 * Download is disabled when editor has unsaved changes.
 */
export default function OfferActionMenu({
  canSave,
  canDownload,
  onSave,
  onDownload,
  disabled,
  useReservationConfirmationDownloadLabel,
  useCancellationConfirmationDownloadLabel = false,
  isEditorDirty
}: OfferActionMenuProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  // Don't show menu if neither action is available
  if (!canSave && !canDownload) return null;

  return (
    <>
      <Button
        variant="contained"
        color="secondary"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        disabled={disabled}
        sx={{ minWidth: 'auto', px: 1 }}
      >
        <ArrowDropDownIcon />
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin=
        {{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {canSave && (
          <MenuItem onClick={() => { setAnchorEl(null); onSave(); }}>
            <ListItemIcon>
              <SaveIcon fontSize="medium" />
            </ListItemIcon>
            <ListItemText>{i18next.t('offer-save-changes-epilo')}</ListItemText>
          </MenuItem>
        )}
        {canDownload && (
          <MenuItem
            onClick={() => { setAnchorEl(null); onDownload(); }}
            disabled={isEditorDirty}
          >
            <ListItemIcon>
              <DownloadIcon fontSize="medium" />
            </ListItemIcon>
            <ListItemText>
              {useCancellationConfirmationDownloadLabel
                ? i18next.t('offer-cancellation-confirmation-process-download')
                : useReservationConfirmationDownloadLabel
                  ? i18next.t('offer-confirmation-process-download')
                  : i18next.t('offer-process-download')}
            </ListItemText>
          </MenuItem>
        )}
      </Menu>
    </>
  );
}